import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/pl";
import axios from "../../../axiosConfig";
import TrainerLayout from "../TrainerLayout";
import { TrainerContext } from "../../../context/TrainerContext";
import { CalendarProvider, useCalendar } from "../../../context/CalendarContext";
import CalendarGrid from "./CalendarGrid";
import PendingRequestsModal from "../PendingRequestsModal";
import BookTrainingModal from "../BookTrainingModal";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import EditTrainingModal from "../EditTrainingModal";
import MobileTrainerCalendar from "./mobile/MobileTrainerCalendar";
import { mergeCalendarEvents } from "../../../utils/calendar/calendarHelpers";
import { useCalendarData } from "../../../hooks/calendar/useCalendarData";
import TrainingDetailsModal from "./modals/TrainingDetailsModal";
import EditTemporaryLockModal from "./modals/EditTemporaryLockModal";
import "../../../styles/TrainerCalendar.css";
import EditTrainingLockModal from "./modals/EditTrainingLockModal";
import { snapToGrid } from "../../../utils/calendar/timeCalculations";

dayjs.locale("pl");
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const CalendarContent = () => {
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();

    // Existing states
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => dayjs().startOf("isoWeek"));

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [trainingToEdit, setTrainingToEdit] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedTrainingDetails, setSelectedTrainingDetails] = useState(null);
    const [editLockModalOpen, setEditLockModalOpen] = useState(false);
    const [lockToEdit, setLockToEdit] = useState(null);

    // Lock mode states
    const [isLockMode, setIsLockMode] = useState(false);
    const [selectedLockSlots, setSelectedLockSlots] = useState([]);

    // Temporary lock edit modal states
    const [editTempLockModalOpen, setEditTempLockModalOpen] = useState(false);
    const [tempLockToEdit, setTempLockToEdit] = useState(null);

    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);

    // Use calendar data hook
    const {
        trainers,
        services,
        halls,
        workingHours,
        confirmedTrainings,
        pendingBookings,
        groupTrainings,
        lockedSlots,
        schoolSettings,
        allEvents,
        isLoading,
        refreshCalendar,
        bookTraining,
        updateTraining,
        deleteTraining,
        lockTimeSlots,
        deleteLock,
        updateLock
    } = useCalendarData({
        trainerId: selectedTrainer?.id,
        schoolId: selectedTrainer?.schoolId || trainer?.schoolId,
        weekStart: currentWeekStart,
    });

    // Mobile detection - include iPad Air
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024); // Changed from 1034 to 1024 to include iPad Air
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Set initial trainer from context
    useEffect(() => {
        if (trainer) setSelectedTrainer(trainer);
    }, [trainer]);

    // Memoized navigation callbacks
    const goToPrevWeek = useCallback(() => {
        setCurrentWeekStart(prev => {
            const newWeek = dayjs(prev).subtract(1, "week");
            console.log('goToPrevWeek - new week:', newWeek.format('YYYY-MM-DD'));
            return newWeek;
        });
    }, []);

    const goToNextWeek = useCallback(() => {
        setCurrentWeekStart(prev => {
            const newWeek = dayjs(prev).add(1, "week");
            console.log('goToNextWeek - new week:', newWeek.format('YYYY-MM-DD'));
            return newWeek;
        });
    }, []);

    // Memoized modal handlers
    const openModal = useCallback((slotTime, slotEndTime = null) => {
        console.log("Opening pending requests modal for slot:", slotTime.format("HH:mm:ss"), "to", slotEndTime?.format("HH:mm:ss"));
        setSelectedSlot({
            startTime: slotTime.clone(),
            endTime: slotEndTime ? slotEndTime.clone() : slotTime.clone().add(60, "minute")
        });
        setModalOpen(true);
    }, []);

    const openBookingModal = useCallback((startTime, endTime = null) => {
        console.log("Opening booking modal for time:", startTime.format("HH:mm"));
        setSelectedBookingSlot({
            startTime: startTime.clone(),
            endTime: endTime || startTime.clone().add(60, "minute"),
        });
        setBookingModalOpen(true);
    }, []);

    // Handle event click
    const handleEventClick = useCallback((event) => {
        if (event.bookingCount && event.bookingCount > 0) {
            // Open pending requests modal
            openModal(dayjs(event.slot || event.startTime), dayjs(event.slotEndTime || event.endTime));
        } else if (event.confirmed && event.dogDTO) {
            // Open details modal for confirmed trainings
            setSelectedTrainingDetails(event);
            setDetailsModalOpen(true);
        } else if (event.isLocked && !event.isTemporary) {
            // Open edit modal for locks
            setLockToEdit(event);
            setEditLockModalOpen(true);
        } else if (!event.confirmed && !event.isLocked && !event.trainingGroup) {
            // Open booking modal for empty slots
            openBookingModal(dayjs(event.startTime), dayjs(event.endTime));
        }
    }, [openModal, openBookingModal]);

    // Handle details modal close
    const handleDetailsModalClose = useCallback(() => {
        setDetailsModalOpen(false);
        setSelectedTrainingDetails(null);
    }, []);

    const handleEditLockModalClose = useCallback(() => {
        setEditLockModalOpen(false);
        setLockToEdit(null);
    }, []);

    // Handle temporary lock editing
    const handleEditTemporaryLock = useCallback((lockIndex, lockData) => {
        setTempLockToEdit({ lockIndex, lockData });
        setEditTempLockModalOpen(true);
    }, []);

    const handleEditTempLockModalClose = useCallback(() => {
        setEditTempLockModalOpen(false);
        setTempLockToEdit(null);
    }, []);

    const handleSaveTemporaryLock = useCallback((newTimes) => {
        if (tempLockToEdit && selectedLockSlots.length > tempLockToEdit.lockIndex) {
            const updatedLocks = [...selectedLockSlots];
            updatedLocks[tempLockToEdit.lockIndex] = {
                ...updatedLocks[tempLockToEdit.lockIndex],
                startTime: newTimes.startTime,
                endTime: newTimes.endTime,
            };
            setSelectedLockSlots(updatedLocks);
        }
    }, [tempLockToEdit, selectedLockSlots, setSelectedLockSlots]);

    // Create temporary lock and open modal immediately (with 15-min snapping)
    const createTemporaryLockAndOpenModal = useCallback((clickTime) => {
        if (!selectedTrainer?.id) return;

        // Snap the click time to 15-minute intervals
        const snappedStartTime = snapToGrid(clickTime, 15);

        // Create 1-hour lock by default, but also snap the end time
        let endTime = snappedStartTime.add(1, 'hour');

        // Ensure lock doesn't exceed working hours
        const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][snappedStartTime.day()];
        const dayWorkingHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);

        if (dayWorkingHours) {
            const [closeHour, closeMinute] = dayWorkingHours.closingTime.split(':').map(Number);
            const dayEndTime = snappedStartTime.clone().hour(closeHour).minute(closeMinute).second(0);

            if (endTime.isAfter(dayEndTime)) {
                endTime = dayEndTime;
            }
        }

        // Create new lock object
        const newLock = {
            startTime: snappedStartTime.format("YYYY-MM-DDTHH:mm:ss"),
            endTime: endTime.format("YYYY-MM-DDTHH:mm:ss"),
            trainerId: selectedTrainer.id,
        };

        // Add to selectedLockSlots
        const newIndex = selectedLockSlots.length;
        setSelectedLockSlots(prev => [...prev, newLock]);

        // Immediately open edit modal
        setTempLockToEdit({
            lockIndex: newIndex,
            lockData: {
                ...newLock,
                isTemporary: true
            }
        });
        setEditTempLockModalOpen(true);
    }, [selectedTrainer?.id, selectedLockSlots, workingHours]);

    // Handle time slot click (for creating new events or locks)
    const handleTimeSlotClick = useCallback((startTime, endTime) => {
        if (isLockMode) {
            // Universal click-to-edit for lock mode
            createTemporaryLockAndOpenModal(startTime);
        } else {
            // Regular booking flow
            openBookingModal(startTime, endTime);
        }
    }, [isLockMode, createTemporaryLockAndOpenModal, openBookingModal]);

    // Handle edit training
    const handleEditTraining = useCallback((training) => {
        setTrainingToEdit(training);
        setEditModalOpen(true);
    }, []);

    // Handle delete training
    const handleDeleteTraining = useCallback(async (item) => {
        setTrainingToDelete(item);
        setDeleteModalOpen(true);
    }, []);

    // Handle delete lock
    const handleDeleteLock = useCallback(async (lock) => {
        const result = await deleteLock(lock.id);
        if (result.success) {
            alert("Zablokowany slot został usunięty.");
        } else {
            alert("Błąd przy usuwaniu zablokowanego slotu.");
        }
    }, [deleteLock]);

    // Handle event move (drag and drop) - kept for confirmed trainings
    const handleEventMove = useCallback(async (event, newStartTime, newEndTime) => {
        console.log("Moving event:", event, "to", newStartTime.format("HH:mm"));
        alert("Przeciąganie wydarzeń będzie dostępne wkrótce!");
    }, []);

    // Handle event resize - kept for confirmed trainings
    const handleEventResize = useCallback(async (event, newStartTime, newEndTime) => {
        console.log("Resizing event:", event, newStartTime.format("HH:mm"), "-", newEndTime.format("HH:mm"));
        alert("Zmiana rozmiaru wydarzeń będzie dostępna wkrótce!");
    }, []);

    // Handlers for Lock Mode
    const handleSaveLock = useCallback(async () => {
        if (!selectedTrainer?.id || selectedLockSlots.length === 0) return;

        const trainingLockTimes = selectedLockSlots.map(slot => ({
            startTime: slot.startTime,
            endTime: slot.endTime
        }));

        const result = await lockTimeSlots({
            trainerId: selectedTrainer.id,
            trainingLockTimes: trainingLockTimes
        });

        if (result.success) {
            alert("Treningi zablokowane pomyślnie!");
            setIsLockMode(false);
            setSelectedLockSlots([]);
        } else {
            alert("Błąd przy blokowaniu treningów.");
        }
    }, [selectedTrainer?.id, selectedLockSlots, lockTimeSlots]);

    const handleCancelLock = useCallback(() => {
        setIsLockMode(false);
        setSelectedLockSlots([]);
    }, []);

    // Handle trainer selection
    const handleTrainerChange = useCallback((e) => {
        const chosenId = e.target.value;
        const found = trainers.find(t => t.id === chosenId);
        setSelectedTrainer(found || null);
    }, [trainers]);

    // Handle lock mode toggle
    const handleLockModeToggle = useCallback(() => {
        setIsLockMode(true);
        setSelectedLockSlots([]);
    }, []);

    // Handle temporary lock deletion
    const handleDeleteTemporaryLock = useCallback((lockIndex) => {
        const updatedLocks = [...selectedLockSlots];
        updatedLocks.splice(lockIndex, 1);
        setSelectedLockSlots(updatedLocks);
    }, [selectedLockSlots, setSelectedLockSlots]);

    // Memoize calendar events
    const calendarEvents = useMemo(() => {
        const events = [
            ...confirmedTrainings,
            ...groupTrainings,
            ...lockedSlots.map(lock => ({ ...lock, isLocked: true })),
            ...pendingBookings.map(booking => ({
                id: `pending-${booking.slot}`,
                startTime: booking.slot,
                endTime: dayjs(booking.slotEndTime).format("YYYY-MM-DDTHH:mm:ss"),
                bookingCount: booking.bookingCount,
                slot: booking.slot,
            })),
        ];

        // Add temporary lock slots when in lock mode
        if (isLockMode && selectedLockSlots.length > 0) {
            selectedLockSlots.forEach((slot, index) => {
                events.push({
                    id: `temp-lock-${index}`,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    isLocked: true,
                    isTemporary: true,
                });
            });
        }

        return events;
    }, [confirmedTrainings, groupTrainings, lockedSlots, pendingBookings, isLockMode, selectedLockSlots]);

    // Memoize modal close handlers
    const handleModalClose = useCallback(() => setModalOpen(false), []);
    const handleBookingModalClose = useCallback(() => setBookingModalOpen(false), []);
    const handleEditModalClose = useCallback(() => setEditModalOpen(false), []);
    const handleDeleteModalClose = useCallback(() => setDeleteModalOpen(false), []);

    const handleDeleteConfirm = useCallback(async () => {
        let result;

        // Check if we're deleting a lock or a training
        if (trainingToDelete.isLocked) {
            result = await deleteLock(trainingToDelete.id);
            if (result.success) {
                alert("Zablokowany slot został usunięty.");
            } else {
                alert("Błąd przy usuwaniu zablokowanego slotu.");
            }
        } else {
            result = await deleteTraining(trainingToDelete.id);
            if (result.success) {
                alert("Trening usunięty pomyślnie.");
            } else {
                alert("Błąd przy usuwaniu treningu.");
            }
        }

        if (result.success) {
            setDeleteModalOpen(false);
            setTrainingToDelete(null);
        }
    }, [trainingToDelete, deleteTraining, deleteLock]);

    return (
        <TrainerLayout>
            <div className="trainer-calendar-container">
                {/* Modals */}
                {modalOpen && (
                    <PendingRequestsModal
                        trainerId={selectedTrainer?.id}
                        schoolId={selectedTrainer?.schoolId}
                        slotTime={selectedSlot?.startTime}
                        slotEndTime={selectedSlot?.endTime}
                        onClose={handleModalClose}
                        refreshCalendar={refreshCalendar}
                        allowReserveList={schoolSettings?.allowReserveList || false}
                    />
                )}
                {bookingModalOpen && selectedTrainer && (
                    <BookTrainingModal
                        isOpen={bookingModalOpen}
                        onClose={handleBookingModalClose}
                        refreshCalendar={refreshCalendar}
                        trainerId={selectedTrainer.id}
                        schoolId={selectedTrainer.schoolId}
                        slotTime={selectedBookingSlot?.startTime}
                        endTime={selectedBookingSlot?.endTime}
                        services={services}
                        halls={halls}
                    />
                )}
                {editModalOpen && trainingToEdit && selectedTrainer && (
                    <EditTrainingModal
                        isOpen={editModalOpen}
                        onClose={handleEditModalClose}
                        schoolId={selectedTrainer.schoolId}
                        trainingId={trainingToEdit.id}
                        refreshCalendar={refreshCalendar}
                    />
                )}
                {deleteModalOpen && trainingToDelete && (
                    <DeleteConfirmationModal
                        isOpen={deleteModalOpen}
                        onClose={handleDeleteModalClose}
                        onConfirm={handleDeleteConfirm}
                        itemName={
                            trainingToDelete.isLocked
                                ? `zablokowany termin ${dayjs(trainingToDelete.startTime).format("HH:mm")} - ${dayjs(trainingToDelete.endTime).format("HH:mm")}`
                                : `trening ${dayjs(trainingToDelete.startTime).format("HH:mm")} - ${dayjs(trainingToDelete.endTime).format("HH:mm")} dla ${trainingToDelete.dogDTO?.name || 'nieznany'}`
                        }
                    />
                )}
                {detailsModalOpen && selectedTrainingDetails && (
                    <TrainingDetailsModal
                        isOpen={detailsModalOpen}
                        onClose={handleDetailsModalClose}
                        training={selectedTrainingDetails}
                        onEdit={handleEditTraining}
                        onDelete={handleDeleteTraining}
                    />
                )}
                {editLockModalOpen && lockToEdit && (
                    <EditTrainingLockModal
                        isOpen={editLockModalOpen}
                        onClose={handleEditLockModalClose}
                        lock={lockToEdit}
                        refreshCalendar={refreshCalendar}
                        onDelete={handleDeleteTraining}
                    />
                )}
                {editTempLockModalOpen && tempLockToEdit && (
                    <EditTemporaryLockModal
                        isOpen={editTempLockModalOpen}
                        onClose={handleEditTempLockModalClose}
                        lock={tempLockToEdit.lockData}
                        onSave={handleSaveTemporaryLock}
                    />
                )}

                <div className="calendar-header">
                    <h1 className="calendar-title">Kalendarz Trenera</h1>
                </div>

                <div className="calendar-controls">
                    <div className="controls-row">
                        <div className="trainer-picker">
                            <label>Wybierz trenera:</label>
                            <select
                                value={selectedTrainer?.id || ""}
                                onChange={handleTrainerChange}
                            >
                                <option value="">-- wybierz --</option>
                                {trainers.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.firstName} {t.lastName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="lock-mode-controls">
                            {isLockMode ? (
                                <>
                                    <button className="lock-save-btn" onClick={handleSaveLock}>
                                        Zapisz blokady ({selectedLockSlots.length})
                                    </button>
                                    <button className="lock-cancel-btn" onClick={handleCancelLock}>
                                        Anuluj
                                    </button>
                                    <span className="lock-mode-hint">
                                        Kliknij na godzinę aby utworzyć blokadę (15-min odstępy).
                                    </span>
                                </>
                            ) : (
                                <button className="lock-mode-btn" onClick={handleLockModeToggle}>
                                    Zablokuj wybrane godziny
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Calendar View */}
                {isMobile ? (
                    selectedTrainer ? (
                        <MobileTrainerCalendar
                            currentWeekStart={currentWeekStart}
                            selectedTrainer={selectedTrainer}
                            trainers={trainers} // Added prop
                            workingHours={workingHours}
                            pendingBookings={pendingBookings}
                            confirmedTrainings={confirmedTrainings}
                            groupTrainings={groupTrainings}
                            lockedSlots={lockedSlots}
                            isLockMode={isLockMode}
                            selectedLockSlots={selectedLockSlots}
                            setSelectedLockSlots={setSelectedLockSlots}
                            services={services}
                            halls={halls}
                            schoolSettings={schoolSettings}
                            onPrevWeek={goToPrevWeek}
                            onNextWeek={goToNextWeek}
                            refreshCalendar={refreshCalendar}
                            onSaveLock={handleSaveLock}
                            onCancelLock={handleCancelLock}
                            onEditTemporaryLock={handleEditTemporaryLock}
                            onDeleteTemporaryLock={handleDeleteTemporaryLock}
                            onTrainerChange={handleTrainerChange} // Added prop
                            onLockModeToggle={handleLockModeToggle} // Added prop
                        />
                    ) : (
                        <div className="mobile-calendar-placeholder">
                            <div className="mobile-no-trainer">
                                <div className="mobile-no-trainer-icon">👤</div>
                                <div className="mobile-no-trainer-text">Wybierz trenera aby zobaczyć kalendarz</div>
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        {/* Desktop Calendar Navigation */}
                        <div className="calendar-navigation">
                            <div className="week-navigation-left">
                                <button onClick={goToPrevWeek}>‹</button>
                            </div>
                            <div className="current-week-info">
                                {currentWeekStart.format('DD MMM')} - {currentWeekStart.add(6, 'day').format('DD MMM YYYY')}
                            </div>
                            <div className="week-navigation-right">
                                <button onClick={goToNextWeek}>›</button>
                            </div>
                        </div>

                        {/* Desktop Calendar Grid */}
                        {isLoading ? (
                            <div className="calendar-loading" style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '400px',
                                fontSize: '16px',
                                color: '#6b7280'
                            }}>
                                Ładowanie kalendarza...
                            </div>
                        ) : (
                            <CalendarGrid
                                selectedTrainer={selectedTrainer}
                                workingHours={workingHours}
                                events={calendarEvents}
                                currentWeekStart={currentWeekStart}
                                onEventClick={handleEventClick}
                                onEventEdit={handleEditTraining}
                                onEventDelete={handleDeleteTraining}
                                onTimeSlotClick={handleTimeSlotClick}
                                onEventMove={handleEventMove}
                                onEventResize={handleEventResize}
                                isLockMode={isLockMode}
                                selectedLockSlots={selectedLockSlots}
                                setSelectedLockSlots={setSelectedLockSlots}
                                onEditTemporaryLock={handleEditTemporaryLock}
                                onDeleteTemporaryLock={handleDeleteTemporaryLock}
                            />
                        )}
                    </>
                )}
            </div>
        </TrainerLayout>
    );
};

// Main Calendar component wrapped with CalendarProvider
const Calendar = () => {
    return (
        <CalendarProvider>
            <CalendarContent />
        </CalendarProvider>
    );
};

export default Calendar;